import {
  ErrorBoundary,
  captureException as sentryCapture,
  captureMessage,
  init,
} from "@sentry/react";

// Sentry 에러 타입 정의
export const SentryErrorType = {
  ROUTING_ERROR: "routing_error",
  API_ERROR: "api_error",
  RATE_LIMIT: "rate_limit",
  RENDER_ERROR: "render_error",
  UNHANDLED_ERROR: "unhandled_error",
} as const;

export type SentryErrorType =
  (typeof SentryErrorType)[keyof typeof SentryErrorType];

// Sentry 에러 레벨
export const SentryErrorLevel = {
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
} as const;

export type SentryErrorLevel =
  (typeof SentryErrorLevel)[keyof typeof SentryErrorLevel];

// 에러 캡처 헬퍼 함수
export function captureException(
  error: Error | unknown,
  options?: {
    type?: SentryErrorType;
    level?: SentryErrorLevel;
    statusCode?: number;
    context?: Record<string, any>;
  },
) {
  const {
    type,
    level = SentryErrorLevel.ERROR,
    statusCode,
    context,
  } = options || {};

  sentryCapture(error, {
    level,
    tags: {
      ...(type && { type }),
      ...(statusCode && { status_code: statusCode }),
    },
    ...(context && { extra: context }),
  });
}

// 특정 에러 타입별 헬퍼 함수
export function captureApiError(error: any, statusCode: number) {
  const level =
    statusCode >= 500 ? SentryErrorLevel.ERROR : SentryErrorLevel.WARNING;

  captureException(error, {
    type: SentryErrorType.API_ERROR,
    level,
    statusCode,
  });
}

export function captureRoutingError(error: Error) {
  captureException(error, {
    type: SentryErrorType.ROUTING_ERROR,
    level: SentryErrorLevel.WARNING,
  });
}

export function captureRateLimitError(error: Error) {
  captureException(error, {
    type: SentryErrorType.RATE_LIMIT,
    level: SentryErrorLevel.WARNING,
    statusCode: 429,
  });
}

export function initSentry() {
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.MODE === "production",

    // sampleRate: 0.2, // 나중에 트래픽 많아지면 활성화
    // tracesSampleRate: 0.1, // 10%의 트랜잭션만 추적
  });
}

export { ErrorBoundary as SentryErrorBoundary, captureMessage };

